import { CommonModule, NgOptimizedImage } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
  OnDestroy,
  OnInit,
  signal,
  ViewChild,
  WritableSignal
} from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { HubConnectionState } from '@microsoft/signalr';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { UntilDestroy } from '@ngneat/until-destroy';
import { I18NextPipe, I18NextService } from 'angular-i18next';
import {
  AlertComponent,
  AutoFocusDirective,
  ButtonComponent,
  CheckboxComponent,
  InputComponent,
  InputErrorsComponent,
  MbsPopupType,
  MbsSize,
  ModalComponent,
  ModalFooterDirective,
  ModalService,
  SelectComponent
} from 'mbs-ui-kit';
import { IncorrectTimeAlertComponent, RecoveryCodeModalComponent, TfaTimerComponent } from 'mbs-ui-shared@components';
import { ReserveAuthConfirmComponent } from 'mbs-ui-shared@components/reserve-auth-confirm/reserve-auth-confirm.component';
import { getPeriods } from 'mbs-ui-shared@constants';
import { TFAFacade } from 'mbs-ui-shared@facades/tfa.facade';
import { PostMessageTwoFactorAuthenticationType, TFAStatus, TwoFactorAuthActionType } from 'mbs-ui-shared@models';
import { QrCode, ReserveAuthSettings } from 'mbs-ui-shared@models/tfa';
import { SignalRService } from 'mbs-ui-shared@modules';
import { ConfigurationService } from 'mbs-ui-shared@services/configuration.service';
import { TFAService } from 'mbs-ui-shared@services/tfa.service';
import { getInvalidDiffTime, getSecondsFromDate } from 'mbs-ui-shared@utils';
import { catchError, finalize, mergeMap, noop, of, take } from 'rxjs';
import { map } from 'rxjs/operators';

@UntilDestroy()
@Component({
  selector: 'mbs-two-factor-approve-modal',
  templateUrl: './two-factor-approve-modal.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AutoFocusDirective,
    CommonModule,
    IncorrectTimeAlertComponent,
    TfaTimerComponent,
    ButtonComponent,
    ReactiveFormsModule,
    FormsModule,
    ReserveAuthConfirmComponent,
    NgOptimizedImage,
    AlertComponent,
    I18NextPipe,
    InputErrorsComponent,
    ModalComponent,
    CheckboxComponent,
    InputComponent,
    SelectComponent,
    ModalFooterDirective
  ]
})
export class TwoFactorApproveModalComponent implements OnInit, OnDestroy {
  private cdr = inject(ChangeDetectorRef);
  private TFAService = inject(TFAService);
  private TFAFacade = inject(TFAFacade);
  private i18 = inject(I18NextService);
  private modal = inject(ModalService);
  private signalR = inject(SignalRService);
  private activeModal = inject(NgbActiveModal);
  private config = inject(ConfigurationService);
  private destroyRef = inject(DestroyRef);

  public readonly MbsSize = MbsSize;
  public isReserveAuth = false;
  public isCodeAuth = false;
  public isDeclined = false;
  public isLoading = false;
  public isReserveAuthSettingsLoading = false;
  public isRecoveryCode = false;
  public recoveryCodeMask = {
    mask: '****-****-****',
    blocks: {
      '*': {
        mask: '*',
        prepare: function (char) {
          return char === ' ' ? '' : char;
        }
      }
    }
  };
  public googleCode = new FormControl(null, Validators.required);
  public recoveryCode = new FormControl(null, Validators.required);
  public isTimeExpired = false;
  public TFAParams = null;
  public readonly alertType = MbsPopupType;
  public reserveAuthSettings: ReserveAuthSettings;

  public size = MbsSize.sm;
  private tfaErrorObj = { tfa: { message: 'Invalid code' } };

  public periods = getPeriods(this.i18);
  public selectedPeriod = new FormControl(60);
  public isNotAskTFA = false;

  public isInvalidLocalTime = false;
  public getInvalidDiffTime = getInvalidDiffTime;
  public qrCode: WritableSignal<QrCode & { loaded: boolean }> = signal({
    loaded: false,
    qrCodeUrl: null,
    timeExpired: null
  });

  public titles = {
    [TwoFactorAuthActionType.EmergencyLogin]: 'Login to MSP Management Console',
    [TwoFactorAuthActionType.Login]: 'Login to MSP Management Console',
    [TwoFactorAuthActionType.TurnOnTwoFactorAuth]: 'Enable Two-Factor Authentication',
    [TwoFactorAuthActionType.TurnOffTwoFactorAuth]: 'Disable Two-Factor Authentication',
    [TwoFactorAuthActionType.ResetAlternativeCodes]: 'Reset 2FA recovery codes',
    [TwoFactorAuthActionType.RmmAction]: 'RMM action',
    [TwoFactorAuthActionType.DropUser]: 'Delete user',
    [TwoFactorAuthActionType.DropCloudAccount]: 'Delete a Storage Account',
    [TwoFactorAuthActionType.ChangeEmail]: 'Change provider email Address',
    [TwoFactorAuthActionType.ChangePassword]: 'Change password',
    [TwoFactorAuthActionType.DeleteCapacityReportUser]: 'Delete data from Capacity Report',
    [TwoFactorAuthActionType.DeleteCapacityReportComputer]: 'Delete data from Capacity Report',
    [TwoFactorAuthActionType.PasswordRecovery]: 'Password recovery',
    [TwoFactorAuthActionType.PasswordRecoveryOnWizard]: 'Password recovery',
    [TwoFactorAuthActionType.ReserveAuthorizationMethod]: 'Alternative authentication method'
  };

  @ViewChild(ModalComponent, { static: true }) baseModal: ModalComponent;

  ngOnInit(): void {
    this.init(this.baseModal.data);

    this.checkInvalidDate();

    this.signalR.authorizedZoneConnection.on('OnTwoFactorAuthProcessCompleted', (res) => this.handleTFAResponse(res));
    this.handleNoConnection();

    if (this.showQrCode()) {
      this.getQrCode(this.TFAParams.authId).subscribe((res) => {
        this.qrCode.set({
          ...this.qrCode(),
          ...res
        });
      });
    }
  }

  handleNoConnection() {
    if (this.signalR.authorizedZoneConnection.state === HubConnectionState.Connected) {
      return;
    }

    this.TFAFacade.handleTFAPolling(this.TFAParams.authId, (response) => this.handleTFAResponse(response), this.destroyRef);
  }

  getQrCode(authId: string) {
    return this.TFAService.getQrCode(authId).pipe(
      finalize(() => {
        this.qrCode.set({
          ...this.qrCode(),
          loaded: true
        });
      })
    );
  }

  handleTFAResponse(res: TFAStatus) {
    if (res.resolution === 2) {
      this.isDeclined = true;
      this.cdr.detectChanges();
      return;
    }

    if (this.isNotAskTFA) {
      this.doNotDisturb();
    }

    this.isRecoveryCode = false;
    this.baseModal.save(this.baseModal.data.authId);
  }

  init(data) {
    this.TFAParams = {
      ...data,
      isResetCodes: data.actionType === TwoFactorAuthActionType.ResetAlternativeCodes,
      isRecoveryCodeUsageAvailable:
        data.actionType === TwoFactorAuthActionType.TurnOffTwoFactorAuth ||
        data.actionType === TwoFactorAuthActionType.ResetAlternativeCodes,
      isGoogleAuth: data.type === PostMessageTwoFactorAuthenticationType.Google,
      isTFADisableAction: data.actionType === TwoFactorAuthActionType.TurnOffTwoFactorAuth
    };
  }

  doNotDisturb() {
    this.TFAService.doNotDisturb(this.TFAParams.authId, this.selectedPeriod.value).pipe(take(1)).subscribe();
  }

  tryAgain() {
    this.isLoading = true;

    this.TFAService.tryAgain(this.TFAParams.authId)
      .pipe(
        mergeMap((result) => {
          const qrCode$ = this.showQrCode() ? this.getQrCode(result.authId) : of(null);

          return qrCode$.pipe(map((qrCode) => ({ result, qrCode })));
        }),
        finalize(() => {
          this.isLoading = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe(({ result, qrCode }) => {
        this.TFAParams.timeExpired = result.timeExpired;
        this.TFAParams.authId = result.authId;

        qrCode &&
          this.qrCode.set({
            ...this.qrCode(),
            ...qrCode
          });

        this.checkInvalidDate();
      });
  }

  private setReserveAuthSettings(callback: () => void) {
    this.isReserveAuthSettingsLoading = true;
    this.TFAService.getReserveAuthSettings(this.TFAParams.authId)
      .pipe(
        finalize(() => {
          this.isReserveAuthSettingsLoading = false;
          callback();
        })
      )
      .subscribe((res) => {
        this.reserveAuthSettings = res;
        this.reserveAuthSettings?.enabled && this.activeModal.update({ size: MbsSize.md });
        this.cdr.markForCheck();
      });
  }

  private checkInvalidDate() {
    this.isTimeExpired = false;
    this.isInvalidLocalTime = getSecondsFromDate(this.TFAParams.timeExpired) < 0;

    this.cdr.detectChanges();
  }

  public toggleAuthType() {
    this.isInvalidLocalTime = false;

    const toggle = () => {
      this.isCodeAuth = !this.isCodeAuth;
      this.cdr.markForCheck();
    };

    if (!this.config.get('featureFlags')?.ReserveAuthMethod) {
      toggle();
    } else {
      this.setReserveAuthSettings(toggle);
    }
  }

  private openRecoveryCodeModal(codes: Array<string>) {
    this.modal
      .openCustom(RecoveryCodeModalComponent, {
        data: {
          title: 'Reset 2FA recovery codes',
          codes
        },
        size: MbsSize.sm
      })
      .finally(noop);
  }

  public confirmRecoveryCode() {
    this.isLoading = true;
    this.isRecoveryCode = true;
    this.recoveryCode.setErrors(null);

    this.TFAService.confirmAlternativeCode(this.recoveryCode.value, this.TFAParams.authId)
      .pipe(catchError((error) => of(error)))
      .subscribe((result) => this.handleRecoveryCodeConfirmation(result));
  }

  public toggleIsReserveAuth() {
    this.isReserveAuth = !this.isReserveAuth;
  }

  private handleRecoveryCodeConfirmation(result) {
    if (!result.isValid) {
      this.handleInvalidRecoveryCode();
      this.cdr.detectChanges();
      this.isLoading = false;
      return;
    }

    if (!this.TFAParams.isResetCodes && result.codes.length) {
      this.openRecoveryCodeModal(result.codes);
    }
  }

  public confirmGoogle() {
    this.googleCode.setErrors(null);
    this.isLoading = true;
    this.TFAService.confirmGoogle(this.googleCode.value, this.TFAParams.authId)
      .pipe(
        take(1),
        catchError(() => {
          this.handleInvalidTFACode();
          return of({ isValid: false });
        })
      )
      .subscribe((result) => {
        if (!result.isValid) {
          this.handleInvalidTFACode();
          this.isLoading = false;
        }
        this.cdr.detectChanges();
      });
  }

  handleInvalidTFACode() {
    this.googleCode.setValue(null);
    this.googleCode.setErrors(this.tfaErrorObj);
  }

  handleInvalidRecoveryCode() {
    this.recoveryCode.setValue(null);
    this.recoveryCode.setErrors(this.tfaErrorObj);
  }

  showQrCode() {
    return !this.TFAParams.isGoogleAuth;
  }

  ngOnDestroy(): void {
    this.signalR.authorizedZoneConnection.off('OnTwoFactorAuthProcessCompleted');
  }
}
